import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useStringsContext } from '../../../../contexts/strings';
import { useArticleNotificationConf } from '../../../../hooks/useArticles';
import { useSegments } from '../../../../hooks/useSegments';
import moment from 'moment';
import PushNotificationPopup, {
  PushNotificationFormPayload,
} from '../../../common/push-notification/push-notification-form';
import PushNotificationColumn from '../../../common/push-notification/push-notification-column';

interface Props {
  article: any;
  selectedAbbrv: string;
}

export default function ArticlesPushNotificationColumn(props: Props) {
  const { strings } = useStringsContext();
  const { article, selectedAbbrv } = props;
  const [openPushNotificationPopup, setOpenPushNotificationPopup] = React.useState(false);
  const { query, createMutation, updateMutation, deleteMutation } = useArticleNotificationConf({
    abbrv: selectedAbbrv,
    id: article.id,
  });
  const { query: segmentQuery } = useSegments(selectedAbbrv);
  const isLoading = query.isLoading || segmentQuery.isLoading;
  const articleNotificationConf = query.data;
  const pushNotificationId = articleNotificationConf?.id;

  if (isLoading) {
    return <>{strings.pushNotifications.loading}</>;
  }

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <PushNotificationColumn
        pushNotification={articleNotificationConf}
        onClick={() => {
          setOpenPushNotificationPopup(true);
        }}
        abbrv={selectedAbbrv}
      />

      {openPushNotificationPopup ? (
        <PushNotificationPopup
          abbrv={selectedAbbrv}
          itemInfo={
            <Alert icon={false} severity="info" color="info" variant="standard">
              <Typography variant="h6">{article.title}</Typography>
              {article.authors.map((author: any) => author.name).join(', ')}
            </Alert>
          }
          content={{
            target: articleNotificationConf?.notification?.target,
            scheduledTime: articleNotificationConf?.scheduledTime
              ? moment(articleNotificationConf.scheduledTime).format('YYYY-MM-DD[T]HH:mm')
              : undefined,
            title: articleNotificationConf?.notification?.title || article.title || '',
            description:
              articleNotificationConf?.notification?.description || article.description || '',
          }}
          segments={segmentQuery.data || []}
          pushNotificationIsSend={articleNotificationConf?.status === 'SENT'}
          onCreate={async (payload: PushNotificationFormPayload) => {
            const { scheduledTime, target, title, description } = payload;
            const res = await createMutation.mutateAsync({
              scheduledTime,
              notification: {
                title,
                description,
                image: article.image,
                articleUrl: article.articleUrl,
                articlesId: article.id,
                target,
              },
            });
            query.refetch();
            return res;
          }}
          onUpdate={(payload: PushNotificationFormPayload) => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const { scheduledTime, target, title, description } = payload;
            return updateMutation.mutateAsync({
              id: pushNotificationId,
              scheduledTime,
              notification: {
                title,
                description,
                image: article.image,
                articleUrl: article.articleUrl,
                articlesId: article.id,
                target,
              },
              createdAt: (articleNotificationConf?.createdAt || moment().format()) as string,
            });
          }}
          onDelete={async () => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const res = await deleteMutation.mutateAsync({
              id: pushNotificationId,
              articleId: article.id,
            });
            query.refetch();
            return res;
          }}
          onClose={() => {
            setOpenPushNotificationPopup(false);
          }}
          isPending={
            createMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending ||
            query.isLoading
          }
          isNew={!pushNotificationId}
        />
      ) : null}
    </Box>
  );
}
